/**
 * 직원 상세 - 학력/기타 탭 (V40.2)
 *
 * Editable: educationLevel, major, educationYears, certificatesText, trainingsText, commentText
 */

import React, { useState } from 'react';
import { GraduationCap, Edit2, Save, X } from 'lucide-react';
import { Employee } from '../../../../shared/types/employee';
import { useEmployeeEditableFieldsMutations } from '../../../../shared/hooks/useEmployeeEditableFieldsMutations';

interface EducationTabProps {
  employee: Employee;
  canEdit: boolean;
  onRefresh: () => void;
}

type EducationLevel = 'HIGH_SCHOOL' | 'COLLEGE_2Y' | 'COLLEGE_3Y' | 'UNIVERSITY' | 'GRADUATE';

const EDUCATION_LEVEL_OPTIONS: { value: EducationLevel | ''; label: string }[] = [
  { value: '', label: '(선택 안 함)' },
  { value: 'HIGH_SCHOOL', label: '고졸' },
  { value: 'COLLEGE_2Y', label: '초대졸(2년)' },
  { value: 'COLLEGE_3Y', label: '초대졸(3년)' },
  { value: 'UNIVERSITY', label: '대졸' },
  { value: 'GRADUATE', label: '대학원졸' },
];

/**
 * 레거시 educationLevel 코드를 표준 코드로 정규화
 * - ASSOCIATE_2 → COLLEGE_2Y
 * - ASSOCIATE_3 → COLLEGE_3Y
 * - BACHELOR → UNIVERSITY
 * - HIGH_SCHOOL → HIGH_SCHOOL (유지)
 */
const normalizeEducationLevel = (value: string | undefined): EducationLevel | '' => {
  if (!value) return '';

  const legacyMapping: Record<string, EducationLevel> = {
    // 레거시 코드 → 표준 코드
    'ASSOCIATE_2': 'COLLEGE_2Y',
    'ASSOCIATE_3': 'COLLEGE_3Y',
    'BACHELOR': 'UNIVERSITY',
    'MASTER': 'GRADUATE',
    // 표준 코드는 그대로 유지
    'HIGH_SCHOOL': 'HIGH_SCHOOL',
    'COLLEGE_2Y': 'COLLEGE_2Y',
    'COLLEGE_3Y': 'COLLEGE_3Y',
    'UNIVERSITY': 'UNIVERSITY',
    'GRADUATE': 'GRADUATE',
  };

  return legacyMapping[value] || '';
};

export function EducationTab({ employee, canEdit, onRefresh }: EducationTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    educationLevel: normalizeEducationLevel(employee.educationLevel),
    major: employee.major || '',
    educationYears: employee.educationYears || 0,
    certificatesText: employee.certificatesText || '',
    trainingsText: employee.trainingsText || '',
    commentText: employee.commentText || '',
  });
  const { updateEditableFields, isLoading } = useEmployeeEditableFieldsMutations();

  const handleEdit = () => {
    setEditedData({
      educationLevel: normalizeEducationLevel(employee.educationLevel),
      major: employee.major || '',
      educationYears: employee.educationYears || 0,
      certificatesText: employee.certificatesText || '',
      trainingsText: employee.trainingsText || '',
      commentText: employee.commentText || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      educationLevel: normalizeEducationLevel(employee.educationLevel),
      major: employee.major || '',
      educationYears: employee.educationYears || 0,
      certificatesText: employee.certificatesText || '',
      trainingsText: employee.trainingsText || '',
      commentText: employee.commentText || '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    // 빈 문자열은 undefined로 변환 (표준 코드만 저장)
    const educationLevelToSave = editedData.educationLevel || undefined;

    const success = await updateEditableFields(employee.id, {
      educationLevel: educationLevelToSave as EducationLevel | undefined,
      major: editedData.major,
      educationYears: editedData.educationYears,
      certificatesText: editedData.certificatesText,
      trainingsText: editedData.trainingsText,
      commentText: editedData.commentText,
    });

    if (success) {
      alert('학력/기타 정보가 수정되었습니다.');
      setIsEditing(false);
      onRefresh();
    }
  };

  const getEducationLevelLabel = (value: string | undefined) => {
    if (!value) return '-';
    // 레거시 코드를 표준 코드로 정규화 후 라벨 반환
    const normalized = normalizeEducationLevel(value);
    if (!normalized) return '(미등록)'; // 매핑 안 되는 레거시 코드
    const option = EDUCATION_LEVEL_OPTIONS.find((opt) => opt.value === normalized);
    return option?.label || '(미등록)';
  };

  return (
    <div style={containerStyle}>
      {/* 섹션 헤더 */}
      <div style={headerStyle}>
        <GraduationCap size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={titleStyle}>학력/기타</h3>
        {canEdit && !isEditing && (
          <button onClick={handleEdit} style={editButtonStyle}>
            <Edit2 size={16} />
            수정
          </button>
        )}
        {isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={handleCancel} style={cancelButtonStyle} disabled={isLoading}>
              <X size={16} />
              취소
            </button>
            <button onClick={handleSave} style={saveButtonStyle} disabled={isLoading}>
              <Save size={16} />
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>

      {/* 학력 정보 */}
      <div style={gridStyle}>
        {/* 학력 */}
        <div>
          <div style={labelStyle}>학력</div>
          {!isEditing ? (
            <div style={valueStyle}>{getEducationLevelLabel(employee.educationLevel)}</div>
          ) : (
            <select
              value={editedData.educationLevel}
              onChange={(e) =>
                setEditedData({
                  ...editedData,
                  educationLevel: e.target.value as EducationLevel | '',
                })
              }
              style={inputStyle}
            >
              {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 전공 */}
        <div>
          <div style={labelStyle}>전공</div>
          {!isEditing ? (
            <div style={valueStyle}>{employee.major || '-'}</div>
          ) : (
            <input
              type="text"
              value={editedData.major}
              onChange={(e) => setEditedData({ ...editedData, major: e.target.value })}
              style={inputStyle}
              placeholder="전공 입력"
            />
          )}
        </div>

        {/* 교육연한 */}
        <div>
          <div style={labelStyle}>교육연한 (년)</div>
          {!isEditing ? (
            <div style={valueStyle}>{employee.educationYears || 0}</div>
          ) : (
            <input
              type="number"
              value={editedData.educationYears}
              onChange={(e) =>
                setEditedData({ ...editedData, educationYears: parseInt(e.target.value) || 0 })
              }
              style={inputStyle}
              min="0"
            />
          )}
        </div>
      </div>

      {/* 자격증 */}
      <div style={{ marginTop: '20px' }}>
        <div style={labelStyle}>자격증</div>
        {!isEditing ? (
          <div style={textAreaDisplayStyle}>
            {employee.certificatesText || '(없음)'}
          </div>
        ) : (
          <textarea
            value={editedData.certificatesText}
            onChange={(e) =>
              setEditedData({ ...editedData, certificatesText: e.target.value })
            }
            style={textAreaInputStyle}
            placeholder="자격증 정보를 입력하세요 (여러 줄 입력 가능)"
            rows={4}
          />
        )}
      </div>

      {/* 교육이수 */}
      <div style={{ marginTop: '20px' }}>
        <div style={labelStyle}>교육이수</div>
        {!isEditing ? (
          <div style={textAreaDisplayStyle}>
            {employee.trainingsText || '(없음)'}
          </div>
        ) : (
          <textarea
            value={editedData.trainingsText}
            onChange={(e) =>
              setEditedData({ ...editedData, trainingsText: e.target.value })
            }
            style={textAreaInputStyle}
            placeholder="교육이수 정보를 입력하세요 (여러 줄 입력 가능)"
            rows={4}
          />
        )}
      </div>

      {/* 비고 */}
      <div style={{ marginTop: '20px' }}>
        <div style={labelStyle}>비고</div>
        {!isEditing ? (
          <div style={textAreaDisplayStyle}>
            {employee.commentText || '(없음)'}
          </div>
        ) : (
          <textarea
            value={editedData.commentText}
            onChange={(e) =>
              setEditedData({ ...editedData, commentText: e.target.value })
            }
            style={textAreaInputStyle}
            placeholder="비고를 입력하세요 (여러 줄 입력 가능)"
            rows={4}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const containerStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid var(--color-gray-200)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const editButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-success)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-gray-200)',
  color: 'var(--color-gray-700)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--color-gray-500)',
  marginBottom: '4px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  outline: 'none',
};

const textAreaDisplayStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
  whiteSpace: 'pre-wrap',
  padding: '12px',
  background: 'var(--color-gray-50)',
  borderRadius: '6px',
  minHeight: '80px',
};

const textAreaInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '14px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  outline: 'none',
  fontFamily: 'inherit',
  resize: 'vertical',
};
